import useAxios from './useAxios';
import { useRef } from 'react';
import { layoutPreview } from '../routes/stencils';
import type { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import type { TSubmittableData } from '../types/Stencil';
import { checkStatus } from '../routes/backgroundJobs';
import { handleError } from '../utils/error';
import useSubmittableData from './stencil/useSubmittableData';
import { CancellableAxiosInstance } from '@src/utils/axiosInstance';

declare global {
  interface Window {
    displayAlert: (
      alertClass: string,
      alert: string,
      options: Record<string, unknown>
    ) => void;
  }
}

interface PreviewPayload {
  job_id: string;
}

interface PollJobPayload {
  status: string;
  success: string;
  url: string;
  error_message?: string;
}

class StencilPreviewer {
  _axios: AxiosInstance;
  _data: TSubmittableData;
  onSuccess: (url: string) => void;
  onFailure: () => void;

  private _jobId: string | null;
  private _statusPath: string | null;
  private _statusPollInterval: NodeJS.Timer;

  constructor(axios: CancellableAxiosInstance, data: TSubmittableData) {
    this._axios = axios;
    this._data = data;
  }

  start(onSuccess: (url: string) => void, onFailure?: () => void) {
    this.onSuccess = onSuccess;
    this.onFailure = onFailure;

    this._clearAlerts();
    this._axios
      .post<PreviewPayload>(layoutPreview, this._data)
      .then(this._awaitJob)
      .catch(this._previewFailed);
  }

  stop() {
    if (this._jobId) {
      this._axios
        .delete(this._statusPath)
        .then(this._clearPreviewData)
        .catch(handleError);
    }
  }

  _awaitJob = (startResponse: AxiosResponse<PreviewPayload>) => {
    this._jobId = startResponse.data['job_id'];
    this._statusPath = checkStatus(this._jobId);

    const pollForPreview = () => {
      this._axios
        .get<PollJobPayload>(this._statusPath)
        .then(this._handlePoll)
        .catch(this._handleFailedPoll);
    };

    this._statusPollInterval = setInterval(pollForPreview, 2000);
  };

  _handleFailedPoll = (_error: AxiosError) => {
    this._clearPreviewData();
    this._previewFailed();
  };

  _handlePoll = (response: AxiosResponse<PollJobPayload>) => {
    const json = response.data;
    const completionStatus = json['status'];
    const successStatus = json['success'];

    if (completionStatus === 'working') {
      this._previewWorking();
    } else if (completionStatus === 'queued') {
      this._previewQueued();
    } else if (completionStatus === 'complete') {
      this._clearPreviewData();

      if (successStatus === 'true') {
        this._previewCompleted(json['url']);
      } else {
        this._previewFailed(json);
      }
    } else if (completionStatus === 'failed') {
      this._clearPreviewData();
      this._previewFailed();
    } else if (completionStatus === 'stopped') {
      this._clearPreviewData();
      this._previewFailed();
    } else if (completionStatus === 'interrupted') {
      this._clearPreviewData();
      this._previewFailed();
    }
  };

  _previewCompleted = (url: string) => {
    this.onSuccess(url);
  };

  _previewFailed = (responseJson?: PollJobPayload) => {
    const errorMessage = responseJson && responseJson['error_message'];
    const defaultErrorMessage =
      'There was a problem uploading the information. Please click "back" and then "next" to try again. If the problem persists, please let us know at <a href="mailto:support@newlywords.com">support@newlywords.com</a>.';

    window.displayAlert('danger', errorMessage || defaultErrorMessage, {
      html: true,
      containerSelector: '.stencil-form-element'
    });

    $(window).scrollTop(0);

    this.onFailure && this.onFailure();
  };

  _previewQueued = () => {
    // no op
    return undefined;
  };
  _previewWorking = () => {
    // no op
    return undefined;
  };

  _clearPreviewData = () => {
    if (this._statusPollInterval) clearInterval(this._statusPollInterval);

    this._jobId = null;
    this._statusPollInterval = null;
    this._statusPath = null;
  };

  _clearAlerts() {
    $('.stencil-form-element .alert.alert-danger').remove();
  }
}

function useStencilPreviewer() {
  const axios = useAxios();
  const data = useSubmittableData();

  const previewer = useRef(new StencilPreviewer(axios, data));

  return previewer.current;
}

export default useStencilPreviewer;
